import {
  BookOpen,
  MessageCircle,
  FileText,
  Heart as HeartIcon,
  type LucideIcon,
} from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFavorites } from '@/context/FavoritesContext';
import { useNavigation } from '@/context/NavigationContext';
import { useAudio } from '@/context/AudioContext';
import type { FavoriteItem, FavoriteItemType, FavoriteCachedData } from '@/types/favorites';
import type { AudioTrack } from '@/types/audio';
import { UniversalViewer } from '@/components/viewers/UniversalViewer';
import { ImageViewer } from '@/components/ImageViewer';
import { TrackActionMenu } from '@/components/audio';
import { FavoritePrayerModal } from './FavoritePrayerModal';
import { FavoriteMessageModal } from './FavoriteMessageModal';
import { SefariaReaderModal } from '@/components/readers/SefariaReaderModal';
import { FavoriteItemCardContent } from './FavoriteItemCardContent';
import { FavoriteItemCardActions } from './FavoriteItemCardActions';
import { getFile } from '@/services/library';
import { getApprovedPrayer } from '@/services/prayers';
import { detectFileType, type FileType } from '@/types/library';

interface FavoriteItemCardProps {
  item: FavoriteItem;
  onRemove?: (id: string) => void;
  onClosePanel?: () => void;
}

// Icon mapping for item types
const TYPE_ICONS: Record<FavoriteItemType, LucideIcon> = {
  resource: BookOpen,
  message: MessageCircle,
  file: FileText,
  prayer: HeartIcon,
  verse: BookOpen,
};

const TYPE_LABELS: Record<FavoriteItemType, string> = {
  resource: 'Resource',
  message: 'Post',
  file: 'Material',
  prayer: 'Prayer',
  verse: 'Verse',
};

const TYPE_COLORS: Record<FavoriteItemType, { bg: string; text: string; border: string }> = {
  resource: { bg: 'bg-[#f8e8f8]', text: 'text-[#8e4585]', border: 'border-[#e5bce5]' },
  message: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' },
  file: { bg: 'bg-[#f8e8f8]', text: 'text-[#8e4585]', border: 'border-[#e5bce5]' },
  prayer: { bg: 'bg-[#f8e8f8]', text: 'text-[#8e4585]', border: 'border-[#e5bce5]' },
  verse: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
};

/**
 * Convert a verse reference like "Exodus 13:17" to a parsha reference format
 * that the Sefaria API understands (e.g., "Exodus.13")
 */
function parseVerseToParshaRef(verseRef: string): string {
  // Handle format like "Exodus 13:17" or "Genesis 1:1"
  // Convert to "Exodus.13" format (chapter level for fetching context)
  const match = verseRef.match(/^(\w+)\s+(\d+):(\d+)$/);
  if (match) {
    const [, book, chapter] = match;
    return `${book}.${chapter}`;
  }
  // Fallback: replace spaces with dots
  return verseRef.replace(/\s+/g, '.').replace(':', '.');
}

/**
 * Extract the parsha title from the parshaContext
 * e.g., "Parashat Beshalach" -> "Beshalach"
 */
function extractParshaTitle(parshaContext?: string): string {
  if (!parshaContext) return 'Torah Portion';
  // Remove "Parashat " or "Parsha " prefix
  return (
    parshaContext
      .replace(/^Parashat\s+/i, '')
      .replace(/^Parsha\s+/i, '')
      .trim() || 'Torah Portion'
  );
}

export function FavoriteItemCard({ item, onRemove, onClosePanel }: FavoriteItemCardProps) {
  const { removeFavorite, lists, addToList, removeFromList, updateCachedData } = useFavorites();
  const { navigateToChat } = useNavigation();
  const navigate = useNavigate();
  const { currentTrack, isPlaying, pause, resume } = useAudio();
  const cardRef = useRef<HTMLDivElement>(null);
  const actionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Cleanup action timeout on unmount
  useEffect(() => {
    return () => {
      if (actionTimeoutRef.current) {
        clearTimeout(actionTimeoutRef.current);
      }
    };
  }, []);

  // Viewer/modal states
  const [showFileViewer, setShowFileViewer] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [showPrayerModal, setShowPrayerModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showSefariaReader, setShowSefariaReader] = useState(false);

  // Sefaria reader navigation state (for navigating to different parshas from within the reader)
  const [sefariaReaderProps, setSefariaReaderProps] = useState<{
    parshaRef: string;
    parshaTitle: string;
    parshaHebrew: string;
    focusVerseRef: string;
  } | null>(null);

  // Audio action menu state
  const [showTrackActionMenu, setShowTrackActionMenu] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<DOMRect | null>(null);
  const [pendingAudioTrack, setPendingAudioTrack] = useState<AudioTrack | null>(null);

  // Navigate to the correct chat tab with scroll-to-message targeting
  const handleNavigateToChat = () => {
    onClosePanel?.();
    if (item.itemType === 'message' && item.itemId) {
      const chatTab = item.cachedData.chatSource === 'leaders' ? 'leaders' : 'community';
      navigate(`/chat/${chatTab}?messageId=${item.itemId}`);
    } else {
      navigateToChat();
    }
  };

  // Fallback data loading
  const [loading, setLoading] = useState(false);
  const [enhancedData, setEnhancedData] = useState<Partial<FavoriteCachedData>>({});

  const Icon = TYPE_ICONS[item.itemType];
  const typeLabel = TYPE_LABELS[item.itemType];
  const colors = TYPE_COLORS[item.itemType];

  // Merged cached data (original + enhanced from fallback)
  const cachedData = { ...item.cachedData, ...enhancedData };

  // Check if this is an audio file and if it's currently playing
  const isAudioFile = item.itemType === 'file' && cachedData.fileType === 'audio';
  const isThisTrackPlaying = isAudioFile && currentTrack?.id === item.itemId && isPlaying;
  const isThisTrackLoaded = isAudioFile && currentTrack?.id === item.itemId;

  // Auto-sync file favorites when card mounts to catch any changes
  // This ensures the title and other cached data stays fresh
  useEffect(() => {
    if (item.itemType === 'file') {
      const syncFileData = async () => {
        try {
          const file = await getFile(item.itemId);
          if (file) {
            // Check if we need to sync cached data (title or other key fields changed)
            const needsSync =
              item.cachedData.title !== file.title ||
              item.cachedData.preview !== file.description ||
              item.cachedData.fileType !== file.type ||
              item.cachedData.thumbnailUrl !== file.thumbnailUrl ||
              item.cachedData.wideThumbnailUrl !== file.wideThumbnailUrl;

            if (needsSync) {
              await updateCachedData(item.id, {
                title: file.title,
                preview: file.description,
                fileType: file.type,
                url: file.url,
                fileName: file.fileName,
                mimeType: file.mimeType,
                thumbnailUrl: file.thumbnailUrl,
                wideThumbnailUrl: file.wideThumbnailUrl,
              });
            }
          }
        } catch (error) {
          // Silently fail - this is just a background sync
          console.error('[Favorites] Auto-sync failed:', error);
        }
      };

      syncFileData();
    }
    // Only run on mount, not on every update
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.itemId, item.itemType]);

  const handleRemove = async () => {
    try {
      await removeFavorite(item.id);
      onRemove?.(item.id);
    } catch (error) {
      console.error('Failed to remove favorite:', error);
    }
  };

  const handleToggleList = async (listId: string) => {
    const isInList = item.listIds.includes(listId);
    try {
      if (isInList) {
        await removeFromList(item.id, listId);
      } else {
        await addToList(item.id, listId);
      }
    } catch (error) {
      console.error('Failed to update list membership:', error);
    }
  };

  // Fetch fallback data for files if cached URL is missing
  // Also syncs cached data if title or other key fields have changed
  const fetchFileData = async (): Promise<boolean> => {
    setLoading(true);
    try {
      const file = await getFile(item.itemId);
      if (file) {
        const fileType = detectFileType(file.mimeType, file.fileName);

        // Check if we need to sync cached data (title or other key fields changed)
        const needsSync =
          cachedData.title !== file.title ||
          cachedData.preview !== file.description ||
          cachedData.fileType !== file.type ||
          cachedData.thumbnailUrl !== file.thumbnailUrl ||
          cachedData.wideThumbnailUrl !== file.wideThumbnailUrl;

        // Update local enhanced data
        setEnhancedData({
          url: file.url,
          fileType,
          fileName: file.fileName,
          mimeType: file.mimeType,
          title: file.title,
          preview: file.description,
          thumbnailUrl: file.thumbnailUrl,
          wideThumbnailUrl: file.wideThumbnailUrl,
        });

        // Sync to Firestore if data has changed (lazy sync)
        if (needsSync) {
          try {
            await updateCachedData(item.id, {
              title: file.title,
              preview: file.description,
              fileType: file.type,
              url: file.url,
              fileName: file.fileName,
              mimeType: file.mimeType,
              thumbnailUrl: file.thumbnailUrl,
              wideThumbnailUrl: file.wideThumbnailUrl,
            });
          } catch (syncError) {
            console.error('[Favorites] Failed to sync cached data:', syncError);
            // Don't fail the whole operation, just log
          }
        }

        return true;
      } else {
        // File no longer exists - it was deleted
        return false;
      }
    } catch (error) {
      console.error('Failed to fetch file data:', error);
    } finally {
      setLoading(false);
    }
    return false;
  };

  // Fetch fallback data for prayers if missing details
  const fetchPrayerData = async (): Promise<boolean> => {
    // If we already have prayerType, we have enough data
    if (cachedData.prayerType) return true;

    setLoading(true);
    try {
      const prayer = await getApprovedPrayer(item.itemId);
      if (prayer) {
        setEnhancedData({
          prayerType: prayer.type,
          hebrewName: prayer.hebrewName,
          authorName: prayer.addedByName,
          preview: prayer.note,
        });
        return true;
      }
    } catch (error) {
      console.error('Failed to fetch prayer data:', error);
    } finally {
      setLoading(false);
    }
    return false;
  };

  // Handle card click to view item
  const handleCardClick = async () => {
    switch (item.itemType) {
      case 'resource':
        // For resources, itemId is the URL - open in new tab
        window.open(item.itemId, '_blank');
        break;
      case 'file': {
        // For files/materials, fetch data if needed, then open appropriate viewer
        const hasFileData = await fetchFileData();
        if (!hasFileData) return;

        // Need to use state after update - use timeout to let state update
        // Clear any pending timeout to prevent duplicate state updates from rapid clicks
        if (actionTimeoutRef.current) {
          clearTimeout(actionTimeoutRef.current);
        }
        actionTimeoutRef.current = setTimeout(() => {
          // Re-check with latest enhanced data
          const currentUrl = cachedData.url || enhancedData.url;
          const currentFileType = cachedData.fileType || enhancedData.fileType;

          // Validate URL
          if (!currentUrl || currentUrl.trim() === '') {
            console.error('Cannot open file: missing URL');
            return;
          }

          if (currentFileType === 'image') {
            setShowImageViewer(true);
          } else if (currentFileType === 'audio') {
            // For audio files, show action menu or toggle play/pause
            if (isThisTrackLoaded) {
              // Toggle play/pause if already loaded
              if (isThisTrackPlaying) {
                pause();
              } else {
                resume();
              }
            } else {
              // Show action menu for new tracks
              const rect = cardRef.current?.getBoundingClientRect();
              setActionMenuAnchor(rect || null);
              setPendingAudioTrack({
                id: item.itemId,
                url: currentUrl,
                title: cachedData.title || 'Audio',
                fileName: cachedData.fileName,
                mimeType: cachedData.mimeType,
                source: 'favorites',
              });
              setShowTrackActionMenu(true);
            }
          } else {
            setShowFileViewer(true);
          }
        }, 0);
        break;
      }
      case 'prayer':
        // Fetch prayer data if missing, then show modal
        await fetchPrayerData();
        setShowPrayerModal(true);
        break;
      case 'message':
        setShowMessageModal(true);
        break;
      case 'verse':
        // Open Sefaria reader modal and scroll to the favorited verse
        setSefariaReaderProps({
          parshaRef: cachedData.parshaRef || parseVerseToParshaRef(item.itemId),
          parshaTitle: extractParshaTitle(cachedData.parshaContext),
          parshaHebrew: cachedData.parshaHebrew || '',
          focusVerseRef: item.itemId,
        });
        setShowSefariaReader(true);
        break;
    }
  };

  // Check if item is viewable (has required data)
  const isViewable = () => {
    switch (item.itemType) {
      case 'resource':
        return true; // Always viewable - opens URL
      case 'file':
        return !!cachedData.url;
      case 'prayer':
        return true; // Always viewable in modal
      case 'message':
        return true; // Always viewable in modal
      case 'verse':
        return true; // Always viewable - opens Sefaria reader
      default:
        return false;
    }
  };

  return (
    <>
      {/* File viewer (for non-image, non-audio files - audio uses global player) */}
      {showFileViewer && cachedData.url && cachedData.fileType !== 'audio' && (
        <UniversalViewer
          url={cachedData.url}
          type={(cachedData.fileType as FileType) || 'other'}
          title={cachedData.title}
          fileName={cachedData.fileName}
          mimeType={cachedData.mimeType}
          onClose={() => setShowFileViewer(false)}
          fileId={item.itemId}
          favoriteCachedData={cachedData}
        />
      )}

      {/* Image viewer */}
      {showImageViewer && cachedData.url && (
        <ImageViewer
          src={cachedData.url}
          alt={cachedData.title}
          onClose={() => setShowImageViewer(false)}
        />
      )}

      {/* Prayer modal */}
      <FavoritePrayerModal
        isOpen={showPrayerModal}
        onClose={() => setShowPrayerModal(false)}
        name={cachedData.title}
        hebrewName={cachedData.hebrewName}
        note={cachedData.preview}
        prayerType={cachedData.prayerType}
        addedByName={cachedData.authorName}
      />

      {/* Message modal */}
      <FavoriteMessageModal
        isOpen={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        author={cachedData.authorName || 'Community Member'}
        text={cachedData.preview || cachedData.title}
        timestamp={cachedData.itemCreatedAt}
        imageUrl={cachedData.imageUrl}
        imageUrls={cachedData.imageUrls}
        chatSource={cachedData.chatSource}
        onNavigateToChat={handleNavigateToChat}
      />

      {/* Sefaria Reader modal */}
      {showSefariaReader && sefariaReaderProps && (
        <SefariaReaderModal
          parshaRef={sefariaReaderProps.parshaRef}
          parshaTitle={sefariaReaderProps.parshaTitle}
          parshaHebrew={sefariaReaderProps.parshaHebrew}
          onClose={() => {
            setShowSefariaReader(false);
            setSefariaReaderProps(null);
          }}
          focusVerseRef={sefariaReaderProps.focusVerseRef}
          onNavigateToParsha={(newRef, newTitle, newHebrew, newFocusVerse) => {
            // Close and reopen with new parsha
            setSefariaReaderProps({
              parshaRef: newRef,
              parshaTitle: newTitle,
              parshaHebrew: newHebrew,
              focusVerseRef: newFocusVerse,
            });
          }}
        />
      )}

      <div
        ref={cardRef}
        className={`bg-white rounded-xl border p-4 transition-all duration-200 group ${
          isThisTrackPlaying
            ? 'border-[#8e4585] ring-2 ring-[#8e4585]/20 shadow-md'
            : 'border-slate-100 hover:border-slate-200'
        } ${
          isViewable()
            ? 'cursor-pointer hover:shadow-lg hover:-translate-y-0.5 hover:border-[#e5bce5]'
            : ''
        }`}
        onClick={isViewable() ? handleCardClick : undefined}
        role={isViewable() ? 'button' : undefined}
        tabIndex={isViewable() ? 0 : undefined}
        onKeyDown={isViewable() ? (e) => e.key === 'Enter' && handleCardClick() : undefined}
      >
        <FavoriteItemCardContent
          item={item}
          cachedData={cachedData}
          Icon={Icon}
          typeLabel={typeLabel}
          colors={colors}
          loading={loading}
          isAudioFile={isAudioFile}
          isThisTrackPlaying={isThisTrackPlaying}
          isThisTrackLoaded={isThisTrackLoaded}
          isViewable={isViewable()}
        />
        <FavoriteItemCardActions
          item={item}
          lists={lists}
          onRemove={handleRemove}
          onToggleList={handleToggleList}
        />
      </div>

      {/* Track Action Menu for audio files */}
      {pendingAudioTrack && (
        <TrackActionMenu
          track={pendingAudioTrack}
          isOpen={showTrackActionMenu}
          onClose={() => {
            setShowTrackActionMenu(false);
            setPendingAudioTrack(null);
          }}
          sourcePosition={actionMenuAnchor || undefined}
        />
      )}
    </>
  );
}
